/* eslint-disable class-methods-use-this */
/**
 * Google Analytics 4 Helper
 * Wraps gtag.js for GA4 tracking
 */
export class GA4Helper {
  constructor({ measurementId }) {
    this.measurementId = measurementId;
    this.initialized = false;
  }

  init() {
    if (this.initialized || !window.gtag) return;
    this.initialized = true;
  }

  /**
   * Identify user in GA4 via user properties
   * @param {Object} user - User object with id, email, name
   */
  identify(user) {
    if (!window.gtag) return;

    window.gtag('config', this.measurementId, {
      user_id: `user-${user.id}`,
    });

    // Set user properties
    window.gtag('set', 'user_properties', {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name,
      account_id: user.account_id,
    });
  }

  /**
   * Track an event in GA4
   * @param {string} eventName - Event name (should be snake_case)
   * @param {Object} properties - Event parameters
   */
  track(eventName, properties = {}) {
    if (!window.gtag) return;

    // GA4 has a 25 parameter limit per event
    const limitedProps = Object.fromEntries(
      Object.entries(properties).slice(0, 25)
    );

    window.gtag('event', eventName, limitedProps);
  }

  /**
   * Track page view in GA4
   * @param {string} pageName - Page name/path
   * @param {Object} properties - Additional page properties
   */
  page(pageName, properties = {}) {
    if (!window.gtag) return;

    window.gtag('event', 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...properties,
    });
  }

  /**
   * Track ecommerce event (purchase, begin_checkout, etc.)
   * @param {string} eventName - Ecommerce event name
   * @param {Object} data - Ecommerce data with transaction details
   */
  trackEcommerce(eventName, data = {}) {
    if (!window.gtag) return;

    window.gtag('event', eventName, {
      currency: data.currency || 'NGN',
      value: data.value || 0,
      transaction_id: data.transactionId,
      items: data.items || [],
      ...data,
    });
  }

  /**
   * Update GA4 consent settings
   * @param {boolean} analyticsAllowed - Whether analytics consent is granted
   */
  updateConsent(analyticsAllowed) {
    if (!window.gtag) return;

    window.gtag('consent', 'update', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_user_data: analyticsAllowed ? 'granted' : 'denied',
      ad_personalization: analyticsAllowed ? 'granted' : 'denied',
    });
  }

  /**
   * Reset GA4 (for logout)
   */
  reset() {
    if (!window.gtag) return;
    // Reset user ID
    window.gtag('config', this.measurementId, {
      user_id: null,
    });
  }
}
